import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, Input, Select } from '@/components/common';
import { getErrorMessage } from '@/services';
import {
  MAPPING_FIELDS,
  MATCH_STATE_LABEL,
  bankStatementsService,
  type BankMapping,
  type LineFilters,
  type MatchState,
  type StatementPreview,
} from '@/services/bankStatements';
import { useCan } from '@/hooks/useEmployeeRights';
import { formatCurrency, formatDate } from '@/utils';
import { ReconciliationTabs } from './ReconciliationTabs';

/**
 * #688: Reconciliation > Bank statements. Upload (file, bank account, balances) -> preview with column
 * mapping, row errors, duplicates and the opening + credits - debits = closing check -> confirm.
 * Nothing is stored until Confirm. Below: the uploaded statements, and their lines with filters.
 */

const MATCH_VARIANT: Record<MatchState, 'default' | 'info' | 'success' | 'warning'> = {
  unmatched: 'warning',
  suggested: 'info',
  matched: 'success',
  ignored: 'default',
};

export function BankStatementsPage() {
  const canEdit = useCan('reconciliation', 'edit');
  const queryClient = useQueryClient();

  const [account, setAccount] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [opening, setOpening] = useState('');
  const [closing, setClosing] = useState('');
  const [mapping, setMapping] = useState<BankMapping | null>(null);
  const [preview, setPreview] = useState<StatementPreview | null>(null);
  const [confirmMismatch, setConfirmMismatch] = useState(false);
  const [skipErrors, setSkipErrors] = useState(false);
  const [filters, setFilters] = useState<LineFilters>({ direction: '', match_state: '', q: '' });

  const accounts = useQuery({ queryKey: ['bankAccounts'], queryFn: () => bankStatementsService.accounts() });
  const batches = useQuery({ queryKey: ['bankStatementBatches'], queryFn: () => bankStatementsService.batches() });
  const lines = useQuery({
    queryKey: ['bankStatementLines', filters],
    queryFn: () => bankStatementsService.lines(filters),
  });

  const input = (m: BankMapping | null) => ({
    file: file as File,
    bankAccount: account,
    mapping: m,
    openingBalance: opening,
    closingBalance: closing,
    confirmBalanceMismatch: confirmMismatch,
    skipErrorRows: skipErrors,
  });

  const runPreview = useMutation({
    mutationFn: (m: BankMapping | null) => bankStatementsService.preview(input(m)),
    onSuccess: (data) => {
      setPreview(data);
      setMapping(data.mapping ?? data.suggested_mapping);
      if (!opening && data.suggested_opening) setOpening(data.suggested_opening);
      if (!closing && data.suggested_closing) setClosing(data.suggested_closing);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const confirm = useMutation({
    mutationFn: () => bankStatementsService.upload(input(mapping)),
    onSuccess: (r) => {
      toast.success(
        r.new_count === 0
          ? `Nothing new: ${r.duplicate_count} lines were already imported`
          : `${r.new_count} lines imported, ${r.duplicate_count} already imported`
      );
      setPreview(null);
      setFile(null);
      setConfirmMismatch(false);
      setSkipErrors(false);
      queryClient.invalidateQueries({ queryKey: ['bankStatementBatches'] });
      queryClient.invalidateQueries({ queryKey: ['bankStatementLines'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const mismatch = preview?.balance?.state === 'mismatch';
  const hasErrors = (preview?.error_count ?? 0) > 0;
  const canConfirm =
    !!preview?.mapping &&
    (preview.new_count > 0 || preview.duplicate_count > 0) &&
    (!mismatch || confirmMismatch) &&
    (!hasErrors || skipErrors);

  const setFilter = (patch: Partial<LineFilters>) => setFilters((f) => ({ ...f, ...patch, page: 1 }));

  return (
    <div>
      <PageHeader
        title="Bank statements"
        subtitle="Upload the bank's own statement and see every credit and debit it shows"
        breadcrumbs={[{ label: 'Reconciliation' }, { label: 'Bank statements' }]}
      />
      <ReconciliationTabs />

      {canEdit && (
        <Card className="mb-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Upload a statement</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            CSV or Excel. One bank account per file. You see a preview first; nothing is saved until you confirm.
          </p>
          {accounts.data && accounts.data.length === 0 && (
            <p role="alert" className="mt-3 text-sm text-amber-700 dark:text-amber-400">
              No bank account is set up for your broker yet. Add a reconcilable bank account in the ledger first.
            </p>
          )}
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <Select
              label="Bank account"
              name="bank_account"
              value={account}
              placeholder="Choose account"
              options={(accounts.data ?? []).map((a) => ({ value: a.id, label: `${a.name} (${a.code})` }))}
              onChange={(e) => {
                setAccount(e.target.value);
                setPreview(null);
              }}
            />
            <div>
              <label htmlFor="stmt-file" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Statement file
              </label>
              <input
                id="stmt-file"
                type="file"
                accept=".csv,.txt,.xlsx,.xlsm,.xls"
                className="block w-full text-sm text-gray-700 dark:text-gray-300"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setPreview(null);
                }}
              />
            </div>
            <Input label="Opening balance (₹)" name="opening" inputMode="decimal" value={opening} onChange={(e) => setOpening(e.target.value)} />
            <Input label="Closing balance (₹)" name="closing" inputMode="decimal" value={closing} onChange={(e) => setClosing(e.target.value)} />
          </div>
          <div className="mt-3">
            <Button
              onClick={() => runPreview.mutate(null)}
              isLoading={runPreview.isPending}
              disabled={!file || !account}
            >
              Preview
            </Button>
          </div>

          {preview && (
            <div className="mt-6 space-y-4" aria-label="Statement preview">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Columns</h4>
                {!preview.mapping && (
                  <p role="alert" className="text-xs text-amber-700 dark:text-amber-400">
                    The columns could not be worked out. Choose the date column and either Debit and Credit, or one Amount column.
                  </p>
                )}
                <div className="mt-2 grid gap-3 md:grid-cols-3">
                  {MAPPING_FIELDS.map((f) => (
                    <Select
                      key={f.key}
                      label={f.label}
                      name={`map_${f.key}`}
                      value={mapping?.[f.key] ?? ''}
                      placeholder="Not in file"
                      helperText={f.hint}
                      options={preview.headers.map((h, i) => ({ value: h, label: preview.raw_headers[i] || h }))}
                      onChange={(e) => setMapping((m) => ({ ...(m ?? {}), [f.key]: e.target.value || undefined }))}
                    />
                  ))}
                </div>
                <Button className="mt-3" variant="outline" size="sm" onClick={() => runPreview.mutate(mapping)} isLoading={runPreview.isPending}>
                  Apply columns
                </Button>
              </div>

              {preview.mapping && (
                <>
                  <p className="text-sm text-gray-800 dark:text-gray-200">
                    {preview.row_count} rows: <strong>{preview.new_count} new</strong>, {preview.duplicate_count} lines already imported,{' '}
                    {preview.error_count} with errors.
                    {preview.period_start && ` Period ${formatDate(preview.period_start)} to ${formatDate(preview.period_end ?? preview.period_start)}.`}
                  </p>

                  {preview.balance?.state === 'ok' && (
                    <p className="text-sm text-green-700 dark:text-green-400">Opening + credits - debits equals closing.</p>
                  )}
                  {preview.balance?.state === 'not_checked' && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Enter opening and closing balances to check the statement adds up.</p>
                  )}
                  {mismatch && preview.balance && (
                    <div role="alert" className="rounded border border-red-300 p-3 text-sm text-red-700 dark:border-red-700 dark:text-red-400">
                      Opening + credits ({formatCurrency(preview.balance.credits)}) - debits ({formatCurrency(preview.balance.debits)}) is{' '}
                      {formatCurrency(preview.balance.expected_closing ?? '0')}, but the closing balance is {formatCurrency(closing || '0')}{' '}
                      (difference {formatCurrency(preview.balance.difference ?? '0')}).
                      <label className="mt-2 flex items-center gap-2">
                        <input type="checkbox" checked={confirmMismatch} onChange={(e) => setConfirmMismatch(e.target.checked)} />
                        Import anyway
                      </label>
                    </div>
                  )}

                  {hasErrors && (
                    <div role="alert" className="rounded border border-amber-300 p-3 text-sm dark:border-amber-700">
                      <p className="font-medium">Rows that could not be read</p>
                      <ul className="mt-1 list-disc pl-5 text-xs">
                        {preview.errors.slice(0, 20).map((er) => (
                          <li key={er.row}>
                            Row {er.row}: {er.message}
                          </li>
                        ))}
                      </ul>
                      <label className="mt-2 flex items-center gap-2">
                        <input type="checkbox" checked={skipErrors} onChange={(e) => setSkipErrors(e.target.checked)} />
                        Skip these rows and import the rest
                      </label>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="text-left uppercase text-gray-500 dark:text-gray-400">
                          <th className="py-1 pr-3">Date</th>
                          <th className="py-1 pr-3">Narration</th>
                          <th className="py-1 pr-3">Reference</th>
                          <th className="py-1 pr-3 text-right">Debit</th>
                          <th className="py-1 pr-3 text-right">Credit</th>
                          <th className="py-1">State</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.lines.slice(0, 10).map((l) => (
                          <tr key={l.row} className="border-t border-gray-100 dark:border-gray-800">
                            <td className="py-1 pr-3 whitespace-nowrap">{formatDate(l.txn_date)}</td>
                            <td className="py-1 pr-3">{l.narration}</td>
                            <td className="py-1 pr-3 font-mono">{l.reference}</td>
                            <td className="py-1 pr-3 text-right">{Number(l.debit) ? formatCurrency(l.debit) : ''}</td>
                            <td className="py-1 pr-3 text-right">{Number(l.credit) ? formatCurrency(l.credit) : ''}</td>
                            <td className="py-1">{l.duplicate ? 'Already imported' : 'New'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <Button onClick={() => confirm.mutate()} isLoading={confirm.isPending} disabled={!canConfirm}>
                    Confirm and import
                  </Button>
                </>
              )}
            </div>
          )}
        </Card>
      )}

      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Uploaded statements</h3>
        {batches.isError ? (
          <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
            Could not load statements: {getErrorMessage(batches.error)}
          </p>
        ) : (batches.data?.results ?? []).length === 0 ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No statement has been uploaded yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Uploaded</th>
                  <th className="py-2 pr-4">File</th>
                  <th className="py-2 pr-4">Account</th>
                  <th className="py-2 pr-4">Period</th>
                  <th className="py-2 pr-4 text-right">Credits</th>
                  <th className="py-2 pr-4 text-right">Debits</th>
                  <th className="py-2 pr-4">Lines</th>
                  <th className="py-2">Balance</th>
                </tr>
              </thead>
              <tbody>
                {batches.data?.results.map((b) => (
                  <tr key={b.id} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4 whitespace-nowrap">{formatDate(b.created_at)}</td>
                    <td className="py-2 pr-4">{b.source_file}</td>
                    <td className="py-2 pr-4">{b.bank_account_name}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {b.period_start ? `${formatDate(b.period_start)} - ${formatDate(b.period_end ?? b.period_start)}` : ''}
                    </td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(b.total_credits)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(b.total_debits)}</td>
                    <td className="py-2 pr-4">
                      {b.line_count} new, {b.duplicate_count} already imported
                    </td>
                    <td className="py-2">
                      <Badge variant={b.balance_check === 'ok' ? 'success' : b.balance_check === 'mismatch' ? 'warning' : 'default'} size="sm">
                        {b.balance_check === 'ok' ? 'Adds up' : b.balance_check === 'mismatch' ? 'Confirmed mismatch' : 'Not checked'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Lines</h3>
        <div className="mt-3 grid gap-3 md:grid-cols-6">
          <Select
            label="Type"
            name="direction"
            value={filters.direction ?? ''}
            options={[
              { value: '', label: 'Credits and debits' },
              { value: 'credit', label: 'Credits' },
              { value: 'debit', label: 'Debits' },
            ]}
            onChange={(e) => setFilter({ direction: e.target.value as LineFilters['direction'] })}
          />
          <Select
            label="Match"
            name="match_state"
            value={filters.match_state ?? ''}
            options={[
              { value: '', label: 'All' },
              ...(Object.keys(MATCH_STATE_LABEL) as MatchState[]).map((s) => ({ value: s, label: MATCH_STATE_LABEL[s] })),
            ]}
            onChange={(e) => setFilter({ match_state: e.target.value as LineFilters['match_state'] })}
          />
          <Input label="From" name="date_from" type="date" value={filters.date_from ?? ''} onChange={(e) => setFilter({ date_from: e.target.value })} />
          <Input label="To" name="date_to" type="date" value={filters.date_to ?? ''} onChange={(e) => setFilter({ date_to: e.target.value })} />
          <Input label="Min amount" name="min_amount" inputMode="decimal" value={filters.min_amount ?? ''} onChange={(e) => setFilter({ min_amount: e.target.value })} />
          <Input label="Search" name="q" placeholder="Narration or UTR" value={filters.q ?? ''} onChange={(e) => setFilter({ q: e.target.value })} />
        </div>

        {lines.isError ? (
          <p role="alert" className="mt-4 text-sm text-red-600 dark:text-red-400">
            Could not load lines: {getErrorMessage(lines.error)}
          </p>
        ) : (lines.data?.results ?? []).length === 0 ? (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">No lines match.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Narration</th>
                  <th className="py-2 pr-4">Reference</th>
                  <th className="py-2 pr-4 text-right">Debit</th>
                  <th className="py-2 pr-4 text-right">Credit</th>
                  <th className="py-2 pr-4 text-right">Balance</th>
                  <th className="py-2">Match</th>
                </tr>
              </thead>
              <tbody>
                {lines.data?.results.map((l) => (
                  <tr key={l.id} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4 whitespace-nowrap">{formatDate(l.txn_date)}</td>
                    <td className="py-2 pr-4">{l.narration}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{l.reference}</td>
                    <td className="py-2 pr-4 text-right">{Number(l.debit) ? formatCurrency(l.debit) : ''}</td>
                    <td className="py-2 pr-4 text-right">{Number(l.credit) ? formatCurrency(l.credit) : ''}</td>
                    <td className="py-2 pr-4 text-right">{l.balance ? formatCurrency(l.balance) : ''}</td>
                    <td className="py-2">
                      <Badge variant={MATCH_VARIANT[l.match_state]} size="sm">
                        {MATCH_STATE_LABEL[l.match_state]}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{lines.data?.count} lines</p>
          </div>
        )}
      </Card>
    </div>
  );
}
